"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// Neon color configurations - matching the app's neon theme
const NEON_COLORS = [
  { stroke: "#ff1867", glow: "rgba(255, 24, 103, 0.8)", glowOuter: "rgba(255, 24, 103, 0.3)", name: "pink" },
  { stroke: "#00f5ff", glow: "rgba(0, 245, 255, 0.8)", glowOuter: "rgba(0, 245, 255, 0.3)", name: "cyan" },
  { stroke: "#39ff14", glow: "rgba(57, 255, 20, 0.8)", glowOuter: "rgba(57, 255, 20, 0.3)", name: "green" },
  { stroke: "#ff6b35", glow: "rgba(255, 107, 53, 0.8)", glowOuter: "rgba(255, 107, 53, 0.3)", name: "orange" },
  { stroke: "#bf00ff", glow: "rgba(191, 0, 255, 0.8)", glowOuter: "rgba(191, 0, 255, 0.3)", name: "purple" },
];

// Staff line Y positions (5 lines of a musical staff) - centered in 180px height
const _STAFF_LINES = [60, 75, 90, 105, 120];

// Note Y positions (on lines and spaces) - from below staff to above
const NOTE_POSITIONS = [135, 127, 120, 112, 105, 97, 90, 82, 75, 67, 60, 52, 45];

// Note types with visual variations
type NoteType = 'quarter' | 'half' | 'eighth' | 'sixteenth' | 'whole' | 'rest' | 'dotted';

interface MusicNote {
  id: number;
  x: number;
  position: number;
  type: NoteType;
  colorIndex: number;
  isLit: boolean;
}

let noteIdCounter = 0;

// Randomize note properties (but keep x position)
const randomizeNote = (note: MusicNote, newX: number): MusicNote => {
  const types: NoteType[] = ['quarter', 'quarter', 'quarter', 'half', 'eighth', 'eighth', 'sixteenth', 'rest', 'dotted'];
  return {
    ...note,
    id: noteIdCounter++,
    x: newX,
    position: Math.floor(Math.random() * NOTE_POSITIONS.length),
    type: types[Math.floor(Math.random() * types.length)],
    colorIndex: Math.floor(Math.random() * NEON_COLORS.length),
    isLit: false,
  };
};

// Random spacing between notes - balanced density
const getRandomSpacing = (): number => {
  const rand = Math.random();
  if (rand < 0.1) return 32 + Math.random() * 15;    // Tight: 32-47
  if (rand < 0.3) return 47 + Math.random() * 24;    // Medium-tight: 47-71
  if (rand < 0.6) return 71 + Math.random() * 32;    // Medium: 71-103
  if (rand < 0.85) return 103 + Math.random() * 40;  // Wide: 103-143
  return 143 + Math.random() * 55;                    // Very wide: 143-198
};

interface NeonMusicLoaderProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  speed?: "slow" | "normal" | "fast";
  showLabel?: boolean;
}

export function NeonMusicLoader({
  size = "md",
  className,
  speed = "normal",
  showLabel = true,
}: NeonMusicLoaderProps) {
  const notesRef = useRef<MusicNote[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const barOffsetRef = useRef(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  
  const speedMultiplier = useMemo(() => ({
    slow: 0.201,
    normal: 0.324,
    fast: 0.488,
  }), []);

  // 3:2 aspect ratio sizes (40% of original) - canvas will match these exactly
  const sizeConfig = {
    sm: { width: 96, height: 64 },
    md: { width: 132, height: 88 },
    lg: { width: 168, height: 112 },
    xl: { width: 204, height: 136 },
  };

  const config = sizeConfig[size];
  
  // Use config dimensions directly to avoid any stretching
  const viewWidth = config.width;
  const viewHeight = config.height;
  
  // Staff positioning relative to view height
  const staffTop = viewHeight * 0.28;
  const staffSpacing = viewHeight * 0.11;
  const staffLines = useMemo(() => [0, 1, 2, 3, 4].map(i => staffTop + i * staffSpacing), [staffTop, staffSpacing]);

  // Note positions (on and between staff lines)
  const noteYPositions = useMemo(() => Array.from({ length: 13 }, (_, i) =>
    staffTop + staffSpacing * 4.5 - i * (staffSpacing / 2)
  ), [staffTop, staffSpacing]);

  // Bar/measure spacing (adjusted for ~2 bars visible at times)
  const BAR_SPACING = viewWidth * 0.84; // One bar every 84% of width

  // Notes light up at the CENTER of the view
  const LIGHT_UP_X = viewWidth * 0.5;

  // Initialize notes once - fill the view and add buffer beyond
  useEffect(() => {
    const initialNotes: MusicNote[] = [];
    let x = 0;
    const types: NoteType[] = ['quarter', 'quarter', 'quarter', 'half', 'eighth', 'eighth', 'sixteenth', 'rest', 'dotted'];
    const scaledSpacing = viewWidth / 320;
    
    // Generate notes to fill the view plus a buffer beyond the right edge
    while (x < viewWidth + 150) {
      initialNotes.push({
        id: noteIdCounter++,
        x,
        position: Math.floor(Math.random() * noteYPositions.length),
        type: types[Math.floor(Math.random() * types.length)],
        colorIndex: Math.floor(Math.random() * NEON_COLORS.length),
        isLit: x <= LIGHT_UP_X, // Pre-light notes that start in the lit zone
      });
      x += getRandomSpacing() * scaledSpacing;
    }
    notesRef.current = initialNotes;
    barOffsetRef.current = 0;
  }, [viewWidth, LIGHT_UP_X, noteYPositions.length]);

  // Canvas-based animation loop - truly infinite
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas resolution for crisp rendering - match config exactly
    const dpr = window.devicePixelRatio || 1;
    canvas.width = viewWidth * dpr;
    canvas.height = viewHeight * dpr;
    ctx.scale(dpr, dpr);

    // Reduced motion: draw a single static frame with all notes lit
    if (prefersReducedMotion) {
      ctx.clearRect(0, 0, viewWidth, viewHeight);
      // Draw staff lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      staffLines.forEach(y => {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(viewWidth, y);
        ctx.stroke();
      });
      // Draw notes in their current positions, all lit
      notesRef.current.forEach(note => {
        if (note.x >= -20 && note.x <= viewWidth + 20) {
          note.isLit = true;
          drawNote(ctx, note, noteYPositions, staffLines, viewWidth, viewHeight, LIGHT_UP_X);
        }
      });
      return; // No animation loop
    }

    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const moveAmount = deltaTime * speedMultiplier[speed] * (viewWidth / 320);
      
      // Update bar offset for scrolling bar lines
      barOffsetRef.current = (barOffsetRef.current + moveAmount) % BAR_SPACING;

      // Clear canvas
      ctx.clearRect(0, 0, viewWidth, viewHeight);

      // Draw staff lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      staffLines.forEach(y => {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(viewWidth, y);
        ctx.stroke();
      });
      
      // Draw bar lines (measures) - scrolling with the music
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1.5;
      const staffTop_y = staffLines[0];
      const staffBottom_y = staffLines[4];
      for (let barX = -barOffsetRef.current; barX < viewWidth + BAR_SPACING; barX += BAR_SPACING) {
        ctx.beginPath();
        ctx.moveTo(barX, staffTop_y);
        ctx.lineTo(barX, staffBottom_y);
        ctx.stroke();
      }

      // Find the rightmost note position
      let maxX = 0;
      notesRef.current.forEach(note => {
        if (note.x > maxX) maxX = note.x;
      });
      
      // Ensure maxX is always beyond the right edge of the view
      const minSpawnX = viewWidth + 50; // Notes must spawn at least 50px beyond the view
      if (maxX < minSpawnX) {
        maxX = minSpawnX;
      }

      // Update and draw notes
      notesRef.current.forEach(note => {
        // Move note left
        note.x -= moveAmount;

        // Light up when note reaches the CENTER of the view
        if (!note.isLit && note.x <= LIGHT_UP_X) {
          note.isLit = true;
        }

        // Recycle note when it exits the left side
        if (note.x < -30) {
          // Always place recycled notes beyond the right edge
          const spacing = getRandomSpacing() * (viewWidth / 320);
          const newX = Math.max(maxX + spacing, viewWidth + 50 + spacing);
          Object.assign(note, randomizeNote(note, newX));
          maxX = newX;
        }

        // Draw note if in view
        if (note.x >= -20 && note.x <= viewWidth + 20) {
          drawNote(ctx, note, noteYPositions, staffLines, viewWidth, viewHeight, LIGHT_UP_X);
        }
      });

      // No edge fades - clean sharp edges for transparent background

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [speed, viewWidth, viewHeight, BAR_SPACING, LIGHT_UP_X, noteYPositions, speedMultiplier, staffLines, prefersReducedMotion]);

  // Draw a single note on canvas with proper neon effect
  const drawNote = (
    ctx: CanvasRenderingContext2D, 
    note: MusicNote, 
    yPositions: number[], 
    staffLinesY: number[],
    vw: number,
    vh: number,
    _lightUpX: number
  ) => {
    const x = note.x;
    const y = yPositions[note.position] || yPositions[6]; // Default to middle
    const color = NEON_COLORS[note.colorIndex];
    const isLit = note.isLit;
    
    // Fade in from right
    const distFromRight = vw - x;
    const fadeIn = Math.min(1, distFromRight / 50);
    const baseOpacity = isLit ? 1 : fadeIn * 0.4;

    // Scale notes based on view size
    const s = vh / 90; // Base scale factor
    const noteRadius = 4 * s;

    // Rest symbol
    if (note.type === 'rest') {
      const restY = (staffLinesY[1] + staffLinesY[2]) / 2;
      if (isLit) {
        // Outer glow
        ctx.shadowColor = color.glowOuter;
        ctx.shadowBlur = 20;
        ctx.fillStyle = color.glowOuter;
        ctx.fillRect(x - 2.5 * s, restY - 5.5 * s, 5 * s, 11 * s);
        // Main glow
        ctx.shadowColor = color.glow;
        ctx.shadowBlur = 12;
        ctx.fillStyle = color.stroke;
        ctx.fillRect(x - 2 * s, restY - 5 * s, 4 * s, 10 * s);
        // No inner highlight for rests
      } else {
        ctx.fillStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.fillRect(x - 2 * s, restY - 5 * s, 4 * s, 10 * s);
      }
      ctx.shadowBlur = 0;
      return;
    }

    const isHollow = note.type === 'half' || note.type === 'whole';

    // Ledger lines
    ctx.strokeStyle = isLit ? color.stroke : `rgba(255, 255, 255, ${baseOpacity * 0.5})`;
    ctx.lineWidth = 1;
    const ledgerLineWidth = 6 * s;
    if (y < staffLinesY[0] - 2) {
      const ledgerY = staffLinesY[0] - (staffLinesY[1] - staffLinesY[0]);
      if (y <= ledgerY + 2) {
        if (isLit) {
          ctx.shadowColor = color.glow;
          ctx.shadowBlur = 8;
        }
        ctx.beginPath();
        ctx.moveTo(x - ledgerLineWidth, ledgerY);
        ctx.lineTo(x + ledgerLineWidth, ledgerY);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }
    if (y > staffLinesY[4] + 2) {
      const ledgerY = staffLinesY[4] + (staffLinesY[1] - staffLinesY[0]);
      if (y >= ledgerY - 2) {
        if (isLit) {
          ctx.shadowColor = color.glow;
          ctx.shadowBlur = 8;
        }
        ctx.beginPath();
        ctx.moveTo(x - ledgerLineWidth, ledgerY);
        ctx.lineTo(x + ledgerLineWidth, ledgerY);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    }

    if (isLit) {
      // === NEON EFFECT: Multiple layers ===
      
      // Layer 1: Outer glow (large, soft)
      ctx.shadowColor = color.glowOuter;
      ctx.shadowBlur = 30;
      
      // Draw outer glow for note head
      ctx.beginPath();
      ctx.arc(x, y, noteRadius + 2, 0, Math.PI * 2);
      ctx.fillStyle = color.glowOuter;
      ctx.fill();
      
      // Layer 2: Main glow
      ctx.shadowColor = color.glow;
      ctx.shadowBlur = 15;
      
      // Note head
      ctx.beginPath();
      ctx.arc(x, y, noteRadius, 0, Math.PI * 2);
      if (isHollow) {
        ctx.strokeStyle = color.stroke;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        // Subtle inner highlight for hollow notes only
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      } else {
        ctx.fillStyle = color.stroke;
        ctx.fill();
        // No inner circle for filled notes - just keep the solid glow
      }

      // Stem with neon effect (not for whole notes)
      if (note.type !== 'whole') {
        // Glow layer
        ctx.shadowColor = color.glow;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y);
        ctx.lineTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.strokeStyle = color.stroke;
        ctx.lineWidth = 2;
        ctx.stroke();
        // Bright center
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y);
        ctx.lineTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      // Flag for eighth notes with neon
      if (note.type === 'eighth') {
        ctx.shadowColor = color.glow;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 14 * s, x + 8 * s, y - 8 * s);
        ctx.strokeStyle = color.stroke;
        ctx.lineWidth = 2;
        ctx.stroke();
        // Bright center
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      // Double flag for sixteenth notes with neon
      if (note.type === 'sixteenth') {
        ctx.shadowColor = color.glow;
        ctx.shadowBlur = 12;
        ctx.strokeStyle = color.stroke;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 14 * s, x + 8 * s, y - 8 * s);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 14 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 10 * s, x + 8 * s, y - 4 * s);
        ctx.stroke();
        // Bright centers
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 14 * s, x + 8 * s, y - 8 * s);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 14 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 10 * s, x + 8 * s, y - 4 * s);
        ctx.stroke();
      }

      // Dot for dotted notes with neon
      if (note.type === 'dotted') {
        ctx.shadowColor = color.glow;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(x + noteRadius + 3 * s, y, 1.5 * s, 0, Math.PI * 2);
        ctx.fillStyle = color.stroke;
        ctx.fill();
        // No inner highlight for small dots
      }
      
    } else {
      // Non-lit notes: simple white/gray rendering
      ctx.beginPath();
      ctx.arc(x, y, noteRadius, 0, Math.PI * 2);
      if (isHollow) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else {
        ctx.fillStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.fill();
      }

      // Stem
      if (note.type !== 'whole') {
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y);
        ctx.lineTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Flag for eighth notes
      if (note.type === 'eighth') {
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 14 * s, x + 8 * s, y - 8 * s);
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Double flag for sixteenth notes
      if (note.type === 'sixteenth') {
        ctx.strokeStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 18 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 14 * s, x + 8 * s, y - 8 * s);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + noteRadius * 0.8, y - 14 * s);
        ctx.quadraticCurveTo(x + 10 * s, y - 10 * s, x + 8 * s, y - 4 * s);
        ctx.stroke();
      }

      // Dot for dotted notes
      if (note.type === 'dotted') {
        ctx.beginPath();
        ctx.arc(x + noteRadius + 3 * s, y, 1.5 * s, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${baseOpacity})`;
        ctx.fill();
      }
    }

    ctx.shadowBlur = 0;
  };

  return (
    <div className={cn("flex flex-col items-center gap-4", className)}>
      <div
        className="relative overflow-hidden"
        style={{ width: config.width, height: config.height }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: config.width,
            height: config.height,
          }}
        />
      </div>

      {showLabel && (
        <span className="text-xs text-muted-foreground animate-pulse">
          Loading...
        </span>
      )}
    </div>
  );
}

// Full-page loading screen with music animation
export function NeonMusicLoadingScreen({
  message = "Loading...",
}: {
  message?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm">
      {/* Animated background - subtle moving lines like sheet music */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 18px,
              rgba(255, 255, 255, 0.1) 18px,
              rgba(255, 255, 255, 0.1) 20px
            )
          `,
          backgroundSize: "100% 100px",
          animation: "scroll-lines 8s linear infinite",
        }}
      />

      <div className="relative flex flex-col items-center gap-8">
        <NeonMusicLoader size="xl" speed="normal" showLabel={false} />

        <p
          className="text-lg font-medium tracking-wide"
          style={{
            background: "linear-gradient(90deg, #ff1867, #00f5ff, #39ff14, #bf00ff)",
            backgroundSize: "300% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "gradient-shift 4s ease infinite",
          }}
        >
          {message}
        </p>
      </div>

      <style jsx>{`
        @keyframes scroll-lines {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 0 100px;
          }
        }
        @keyframes gradient-shift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
      `}</style>
    </div>
  );
}

// Simple spinner variant that looks like a spinning note
export function NeonNoteSpinner({
  color = "pink",
  size = "md",
  className,
}: {
  color?: "pink" | "cyan" | "green" | "orange" | "purple";
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const colorMap = {
    pink: { stroke: "#ff1867", glow: "rgba(255, 24, 103, 0.6)" },
    cyan: { stroke: "#00f5ff", glow: "rgba(0, 245, 255, 0.6)" },
    green: { stroke: "#39ff14", glow: "rgba(57, 255, 20, 0.6)" },
    orange: { stroke: "#ff6b35", glow: "rgba(255, 107, 53, 0.6)" },
    purple: { stroke: "#bf00ff", glow: "rgba(191, 0, 255, 0.6)" },
  };

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  const c = colorMap[color];

  return (
    <svg
      viewBox="0 0 24 24"
      className={cn(sizeClasses[size], "animate-spin", className)}
      style={{
        filter: `drop-shadow(0 0 4px ${c.glow}) drop-shadow(0 0 8px ${c.glow})`,
      }}
    >
      {/* Musical note shape */}
      <ellipse
        cx="8"
        cy="18"
        rx="4"
        ry="3"
        fill={c.stroke}
        className="opacity-90"
      />
      <line
        x1="12"
        y1="18"
        x2="12"
        y2="4"
        stroke={c.stroke}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 4 Q18 6 16 12"
        fill="none"
        stroke={c.stroke}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

